import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import "../styles/SipCalculator.css";

interface SipCalculatorProps {
  onInvest: (data: {
    investment: number;
    years: number;
    sipType: string;
  }) => void;
}

const MemoChart = React.memo(ReactApexChart);

const ChartSection = React.memo(
  ({ options, series, width, height }: any) => {
    return (
      <MemoChart
        options={options}
        series={series}
        type="donut"
        width={width}
        height={height}
      />
    );
  }
);

function calculateSIP(investmentAmount, years, annualRate, frequency) {
  const periodsPerYear =
    frequency === 'daily' ? 365 : frequency === 'weekly' ? 52 : 12;

  const n = years * periodsPerYear;
  const r = annualRate / 100 / periodsPerYear;

  const futureValue =
    investmentAmount *
    ((Math.pow(1 + r, n) - 1) / r) *
    (1 + r);

  return futureValue.toFixed(2);
}

function formatINR(value: number) {
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

const SliderInput = React.memo(({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  prefix = '',
  suffix = '',
}: any) => {
  const pct = ((value - min) / (max - min)) * 100;

  return (
    <div className="sc-field">
      <div className="sc-field-header">
        <span className="sc-field-label">{label}</span>

        <div className="sc-field-value-wrap">
          {prefix && <span className="sc-field-prefix">{prefix}</span>}
          <input
            className="sc-inline-input"
            type="number"
            value={value}
            step={step}
            onChange={(e) => {
              const val = e.target.value;

              if (val === '') {
                onChange(0);
                return;
              }

              onChange(parseFloat(val));
            }}
            onBlur={(e) => {
              let val = parseFloat(e.target.value);

              if (isNaN(val)) return;

              if (val < min) val = min;
              if (val > max) val = max;

              onChange(val);
            }}
          />
          {suffix && <span className="sc-field-suffix">{suffix}</span>}
        </div>
      </div>

      <div className="sc-slider-track-wrap">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="sc-slider"
          style={{ '--pct': `${pct}%` } as any}
        />
      </div>

      <div className="sc-slider-bounds">
        <span>
          {prefix}
          {formatINR(min)}
          {suffix}
        </span>
        <span>
          {prefix}
          {formatINR(max)}
          {suffix}
        </span>
      </div>
    </div>
  );
});

export default function SipCalculator({ onInvest }: SipCalculatorProps) {
  const [investment, setInvestment] = useState<number>(1000);
  const [years, setYears] = useState<number>(10);
  const [sipType, setSipType] = useState<string>('weekly');
  const [growthRate, setGrowthRate] = useState<number>(12.5);

  const [chartSize, setChartSize] = useState({
    width: 340,
    height: 340,
  });

  const totalValue = useMemo(() => {
    return parseFloat(
      calculateSIP(investment, years, growthRate, sipType)
    );
  }, [investment, years, growthRate, sipType]);

  const investedAmount =
    investment *
    (sipType === 'daily'
      ? years * 365
      : sipType === 'weekly'
        ? years * 52
        : years * 12);

  const estimatedReturns = totalValue - investedAmount;

  const returnsPercent =
    investedAmount > 0
      ? ((estimatedReturns / investedAmount) * 100).toFixed(1)
      : '0';

  useEffect(() => {
    const updateChartSize = () => {
      if (window.innerWidth < 600)
        setChartSize({ width: 260, height: 260 });
      else if (window.innerWidth < 1024)
        setChartSize({ width: 300, height: 300 });
      else setChartSize({ width: 340, height: 340 });
    };

    updateChartSize();
    window.addEventListener('resize', updateChartSize);
    return () => window.removeEventListener('resize', updateChartSize);
  }, []);

  useEffect(() => {
    if (sipType === 'daily') setInvestment(10);
  }, [sipType]);

  const handleInvestNow = () =>
    onInvest({ investment, years, sipType });


  const chartOptions = useMemo(() => ({
    chart: {
      type: 'donut',
      height: 400,
      background: 'transparent',
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 700,
      },
    },

    colors: ['#393939', '#dda838'],

    labels: ['Invested Amount', 'Estimated Returns'],

    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '13px',
      fontWeight: 500,
      labels: {
        colors: '#6b7280',
      },
      markers: {
        width: 10,
        height: 10,
        radius: 12,
        offsetX: -2,
      },
      itemMargin: {
        horizontal: 12,
        vertical: 6,
      },
    },

    dataLabels: {
      enabled: false,
    },

    tooltip: {
      enabled: true,
      theme: 'dark',
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: (val: number) =>
          `₹${new Intl.NumberFormat('en-IN', {
            maximumFractionDigits: 0,
          }).format(val)}`,
      },
    },

    stroke: {
      show: true,
      width: 2,
      colors: ['#ffffff'],
    },

    fill: {
      type: 'gradient',
      gradient: {
        shade: 'dark',
        type: 'smooth',
        shadeIntensity: 0.4,
        gradientToColors: ['#393939', '#dda838'],
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 100],
      },
    },

    plotOptions: {
      pie: {
        expandOnClick: false,
        offsetY: 0,

        donut: {
          size: '66%',

          labels: {
            show: true,

            name: {
              show: true,
              fontSize: '13px',
              color: '#6b7280',
            },

            value: {
              show: true,
              fontSize: '16px',
              fontWeight: 600,
              color: '#111827',
              formatter: (val: string) =>
                `₹${new Intl.NumberFormat('en-IN').format(Number(val))}`,
            },

            total: {
              show: true,
              label: 'Total Value',
              fontSize: '12px',
              color: '#9ca3af',
              formatter: () =>
                `₹${new Intl.NumberFormat('en-IN').format(totalValue)}`,
            },
          },
        },
      },
    },

    states: {
      hover: {
        filter: {
          type: 'lighten',
          value: 0.04,
        },
      },
      active: {
        filter: {
          type: 'darken',
          value: 0.1,
        },
      },
    },

    grid: {
      padding: {
        top: -10,
        bottom: -10,
      },
    },

    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: { height: 320 },
          legend: { fontSize: '12px' },
        },
      },
      {
        breakpoint: 480,
        options: {
          chart: { height: 280 },
          legend: {
            position: 'bottom',
          },
        },
      },
    ],
  }), [totalValue]);


  const chartSeries = [investedAmount, estimatedReturns];

  const [debouncedSeries, setDebouncedSeries] = useState(chartSeries);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSeries(chartSeries);
    }, 80);

    return () => clearTimeout(t);
  }, [chartSeries]);

  return (
    <div className="sc-root">
      <div className="sc-card">
        <div className="sc-left">
          <div className="sc-header">
            <h4 className="sc-title">SIP Returns Calculator</h4>
            <p className="sc-subtitle">
              Estimate your wealth growth with systematic investing
            </p>
          </div>

          <div className="sc-tabs">
            {['daily', 'weekly', 'monthly'].map((type) => (
              <button
                key={type}
                className={`sc-tab ${sipType === type ? 'active' : ''}`}
                onClick={() => setSipType(type)}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>

          <div className="sc-fields">
            <SliderInput
              label="Investment Amount"
              value={investment}
              onChange={setInvestment}
              min={sipType === 'daily' ? 10 : 100}
              max={sipType === 'daily' ? 1500 : 50000}
              step={sipType === 'daily' ? 10 : 100}
              prefix="₹"
            />

            <SliderInput
              label="Time Period"
              value={years}
              onChange={setYears}
              min={1}
              max={40}
              suffix=" yr"
            />

            <SliderInput
              label="Expected Annual Returns"
              value={growthRate}
              onChange={setGrowthRate}
              min={1}
              max={30}
              step={0.5}
              suffix="%"
            />
          </div>

          {/* <div className="sc-summary">
            <div className="sc-summary-stats">
              <div className="sc-stat">
                <div className="sc-stat-label">Invested</div>
                <div className="sc-stat-value">
                  ₹{formatINR(investedAmount)}
                </div>
              </div>
              <div className="sc-stat">
                <div className="sc-stat-label">Returns</div>
                <div className="sc-stat-value">
                  ₹{formatINR(estimatedReturns)}
                </div>
              </div>
              <div className="sc-stat">
                <div className="sc-stat-label">Total</div>
                <div className="sc-stat-value">
                  ₹{formatINR(totalValue)}
                </div>
              </div>
            </div>

            <button className="sc-invest-btn" onClick={handleInvestNow}>
              Invest Now →
            </button>
          </div> */}
        </div>

        <div className="sc-right">
          <div className="sc-chart-title">Portfolio Breakdown</div>

          <ChartSection
            options={chartOptions}
            series={debouncedSeries}
            width={chartSize.width}
            height={chartSize.height}
          />

          <div className="sc-returns-badge">
            ↑ {returnsPercent}% wealth gain over {years}{' '}
            {years === 1 ? 'year' : 'years'}
          </div>
        </div>

        <div className="sc-bottom">
  <div className="sc-summary">
    <div className="sc-summary-stats">
      <div className="sc-stat">
        <div className="sc-stat-label">Invested</div>
        <div className="sc-stat-value">
          ₹{formatINR(investedAmount)}
        </div>
      </div>

      <div className="sc-stat">
        <div className="sc-stat-label">Returns</div>
        <div className="sc-stat-value">
          ₹{formatINR(estimatedReturns)}
        </div>
      </div>

      <div className="sc-stat">
        <div className="sc-stat-label">Total</div>
        <div className="sc-stat-value">
          ₹{formatINR(totalValue)}
        </div>
      </div>
    </div>

    <button className="sc-invest-btn" onClick={handleInvestNow}>
      Invest Now →
    </button>
  </div>

  <p className="sc-disclaimer">
    <strong>Disclaimer:</strong> This calculator is for illustration only and does not represent actual returns.
    Commodities do not have fixed returns and cannot be predicted.
  </p>
</div>
      </div>
    </div>
  );
}